<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import { useScreenSize } from '@/composables/helpers/screen-size'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { isiOS } from '@/helpers/browser'
import ArrowLeft from '@/components/icons/ArrowLeft.vue'
import webapp from '@/services/webapp'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'

const props = withDefaults(defineProps<{
	show: boolean
	closeButton?: boolean
	wrapperClass?: string
	baseModalClass?: string
	outerContentClass?: string
	contentClass?: string
	containerClass?: string
	borderContainerClass?: string
	closeButtonClass?: string
	bgFadeClass?: string
	centered?: boolean
	scrollable?: boolean
	backButton?: boolean
	backButtonClass?: string
	preventClose?: boolean
}>(), {
  closeButton: true,
  wrapperClass: '',
  baseModalClass: '',
  outerContentClass: '',
  contentClass: '',
  containerClass: '',
  borderContainerClass: '',
  closeButtonClass: '',
  bgFadeClass: '',
  centered: false,
  scrollable: false,
  backButton: false,
  backButtonClass: '',
  preventClose: false,
})

const emit = defineEmits(['hide', 'back'])

const { topNumber } = useFullScreenDimensions()

const isMounted = ref(false)
let scrollPosition = 0

const contentSlot = ref<HTMLElement | null>(null)
const appHeight = ref(`${window.innerHeight}px`)
const isIOSBrowser = isiOS()

const topSafeSpace = computed(() => topNumber.value)

const sheetStyle = computed(() => {
  return {
    '--app-height': `${window.innerHeight - topSafeSpace.value}px`,
  }
})

const layoutStyles = computed(() => {
  return { paddingBottom: `${webapp.safeAreaInset.bottom}px` }
})

function setAppHeightOnResize(event: Event) {
  if (!event.target || !('height' in event.target) || !event.target?.height || isNaN(event.target.height as number)) return
  appHeight.value = `${event.target.height}px`
}

onMounted(() => {
  if (props.show) {
    modalOpenEffects()
    setTimeout(() => {
      isMounted.value = true
    }, 50)
  }
  if (!visualViewport || isIOSBrowser) return
  visualViewport.addEventListener('resize', setAppHeightOnResize)
})

watch(() => props.show, show => {
  if (show) {
    modalOpenEffects()
    isMounted.value = true
    appHeight.value = `${window.innerHeight}px`
  } else {
    modalHiddenEffects()
  }
})

onBeforeUnmount(() => {
  modalHiddenEffects()
  if (!visualViewport || isIOSBrowser) return
  visualViewport.removeEventListener('resize', setAppHeightOnResize)
})

function preventScroll(e: TouchEvent) {
  const modal = document.querySelector('.bottom-sheet-container')
  // If the touch started inside a 'dro-overlay', allow scrolling there
  const targetElement = (e.target as Element | null)
  if (targetElement && targetElement.closest && targetElement.closest('.dro-overlay')) return

  if (modal && !modal.contains(e.target as Node) && !props.scrollable) {
    e.preventDefault()  // Prevent scroll only if touch is outside the modal
  }
}

function modalOpenEffects() {
  document.body.classList.add('!overflow-hidden')
  document.body.classList.add('no-overscroll')

  const appWrapper = document.querySelector('#app-wrapper')
  if (appWrapper) {
    appWrapper.classList.add('!overflow-hidden')
    appWrapper.classList.add('no-overscroll')
  }

  document.addEventListener('touchmove', preventScroll, { passive: false })
}

function modalHiddenEffects() {
  onCloseModal()
  //Check if other modal is still open:
  if (document.querySelectorAll('.bottom-sheet-container-instance').length > 1) return
  document.body.classList.remove('!overflow-hidden')
  document.body.classList.remove('no-overscroll')
  const appWrapper = document.querySelector('#app-wrapper')
  if (appWrapper) {
    appWrapper.classList.remove('!overflow-hidden')
    appWrapper.classList.remove('no-overscroll')
  }

  window.scrollTo(0, scrollPosition)  // Restore the scroll position
  document.removeEventListener('touchmove', preventScroll)
}

function onCloseModal() {
  if (props.preventClose) return
  isMounted.value = false
  setTimeout(() => emit('hide'), 300)
}

const { height } = useScreenSize()
watch(height, () => {
  nextTick(() => {
    appHeight.value = `${window.innerHeight}px`
  })
})

</script>
<template>
  <Teleport
    to="#app"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <div
        v-if="isMounted"
        :class="twMerge('fixed inset-0 z-[92] bg-black/20 backdrop-blur-sm', bgFadeClass)"
        @click="onCloseModal"
        aria-hidden="true"
      />
    </transition>
    <transition
      name="fade"
      mode="out-in"
    >
      <div
        v-show="isMounted"
        :class="twMerge('bottom-sheet fixed inset-0 cursor-default z-[92] flex justify-center text-white', wrapperClass)"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="fixed inset-0 z-[91]"
          @click="onCloseModal"
          aria-hidden="true"
        />
        <transition
          name="slide"
          :css="!centered"
        >
          <div
            v-if="isMounted"
            id="base-modal"
            :class="twMerge('bottom-sheet-container overflow-hidden flex flex-col w-full z-[92] fixed bottom-0 max-w-[1023px] items-center',
                            centered && 'top-1/2 -translate-y-1/2 w-[90%] bottom-auto', baseModalClass
            )"
            :style="sheetStyle"
          >
            <slot
              v-if="$slots.baseContent"
              name="baseContent"
            />
            <div
              v-if="show"
              class="bottom-sheet-container-instance -z-[1] absolute w-0 h-0 flex items-center"
            />
            <div
              ref="contentSlot"
              :class="twMerge('flex-1 w-full h-full bg-black rounded-t-[32px] flex flex-col overflow-hidden bg-cover', centered && 'rounded-b-[32px]', outerContentClass)"
            >
              <div
                :class="twMerge('flex-1 w-full h-full bg-gradient-border rounded-t-[32px] flex flex-col relative overflow-hidden', centered && 'rounded-b-[32px] pb-0.5 px-0.5', borderContainerClass)"
              >
                <!--                Back button:-->
                <slot
                  v-if="backButton && $slots.backButton"
                  name="backButton"
                />
                <div
                  v-else-if="backButton"
                  :class="twMerge('absolute w-6 h-6 min-w-6 flex items-center justify-center top-6 left-6 z-[2]', backButtonClass)"
                  @click="$emit('back')"
                >
                  <ArrowLeft class="w-full h-full" />
                </div>

                <!--                Close button: -->
                <slot
                  v-if="closeButton && $slots.closeButton"
                  name="closeButton"
                />
                <div
                  v-else-if="closeButton"
                  :class="twMerge('absolute w-6 h-6 min-w-6 flex items-center justify-center top-6 right-6 z-[2]', closeButtonClass)"
                  @click="onCloseModal"
                >
                  <CrossCircleIcon class="w-full h-full opacity-[87%]" />
                </div>
                <div
                  :class="twMerge('overflow-y-auto px-6 pb-8 pt-12 flex-1 w-full h-full mt-0.5 rounded-t-[32px] bg-black bg-cover', centered && 'rounded-b-[32px]', contentClass)"
                  :style="!centered ?layoutStyles : undefined"
                >
                  <slot />
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped lang="scss">
.bg-gradient-border {
	background: linear-gradient(to right, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 100) 50%, rgba(255, 255, 255, 0.4) 100%);
}

.fade-enter-active {
	transition: opacity 0.15s ease-in;
	opacity: 1;
}

.fade-leave-active {
	transition: opacity 0.15s ease-out;
	opacity: 1;
}

.fade-enter-from {
	opacity: 0;
}

.fade-leave-to {
	opacity: 0;
}

.slide-enter-active {
	transition: transform 0.2s ease-in, opacity 0.2s ease-in;
	transform: translateY(0);
}

.slide-leave-active {
	transition: transform 0.2s ease-out, opacity 0.2s ease-out;
	transform: translateY(0);
}

.slide-enter-from {
	transform: translateY(100%);
}

.slide-leave-to {
	transform: translateY(100%);
}

@media screen and (max-width: 768px) {
	.bottom-sheet-container {
		max-height: calc(var(--app-height) - 32px);
	}
}
</style>
